import React, { useState, useEffect, memo } from 'react';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle,
  Eye,
  EyeOff,
  TestTube,
  Cloud,
  Database,
  Shield,
  Globe,
  Key,
  Bucket,
  Region,
  Upload,
  Download
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const S3Settings = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    accessKeyId: '',
    secretAccessKey: '',
    bucketName: '',
    region: 'us-east-1',
    endpoint: '',
    forcePathStyle: false,
    useAccelerateEndpoint: false,
    encryption: 'AES256',
    publicRead: false,
    cdnDomain: '',
    uploadPrefix: 'uploads',
    maxFileSize: '10485760',
    allowedMimeTypes: 'image/*,application/pdf,text/*,video/*,audio/*'
  });
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    fetchS3Settings();
  }, []);

  const fetchS3Settings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getS3Settings();
      console.log('S3 Settings Response:', response.data);
      if (response.data.success) {
        const fetchedSettings = response.data.data.settings || response.data.data;
        
        const completeSettings = {
          enabled: false,
          accessKeyId: '',
          secretAccessKey: '',
          bucketName: '',
          region: 'us-east-1',
          endpoint: '',
          forcePathStyle: false,
          useAccelerateEndpoint: false,
          encryption: 'AES256',
          publicRead: false,
          cdnDomain: '',
          uploadPrefix: 'uploads',
          maxFileSize: '10485760',
          allowedMimeTypes: 'image/*,application/pdf,text/*,video/*,audio/*',
          ...fetchedSettings
        };
        
        console.log('Complete settings:', completeSettings);
        setSettings(completeSettings);
      }
    } catch (error) {
      console.error('Error fetching S3 settings:', error);
      setSettings({
        enabled: false,
        accessKeyId: '',
        secretAccessKey: '',
        bucketName: '',
        region: 'us-east-1',
        endpoint: '',
        forcePathStyle: false,
        useAccelerateEndpoint: false,
        encryption: 'AES256',
        publicRead: false,
        cdnDomain: '',
        uploadPrefix: 'uploads',
        maxFileSize: '10485760',
        allowedMimeTypes: 'image/*,application/pdf,text/*,video/*,audio/*'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key) => (value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveMessage('');
    setSaveStatus('');
    
    try {
      const response = await adminAPI.updateS3Settings(settings);
      
      if (response.data.success) {
        setSaveMessage('S3 settings saved successfully!');
        setSaveStatus('success');
      } else {
        setSaveMessage('Failed to save S3 settings. Please try again.');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving S3 settings:', error);
      setSaveMessage('Error saving S3 settings. Please check your connection and try again.');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await adminAPI.testS3Connection(settings);
      
      if (response.data.success) {
        setSaveMessage('S3 connection test successful!');
        setSaveStatus('success');
      } else {
        setSaveMessage('S3 connection test failed. Please check your credentials.');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('S3 connection test error:', error);
      setSaveMessage(`S3 connection test failed: ${error.response?.data?.message || error.message}`);
      setSaveStatus('error');
    } finally {
      setTesting(false);
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 5000);
    }
  };

  const InputField = memo(({ label, type = 'text', value, onChange, placeholder, required = false, disabled = false, description }) => {
    const [localValue, setLocalValue] = useState(value ?? '');

    useEffect(() => {
      setLocalValue(value ?? '');
    }, [value]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const commit = () => {
      if (localValue !== (value ?? '')) {
        onChange(localValue);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        commit();
      }
    };

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50"
        />
        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
    );
  });

  const ToggleField = memo(({ label, value, onChange, description }) => {
    const boolValue = Boolean(value);
    
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {label}
            </label>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange(!boolValue)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              boolValue ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                boolValue ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    );
  });

  const SelectField = memo(({ label, value, onChange, options }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  ));

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Cloud className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
          AWS S3 Storage Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure AWS S3 storage for file uploads. When enabled, all uploads will be stored in S3 instead of local storage.
        </p>
      </div>

      {/* Debug (dev-only) */}
      {process.env.NODE_ENV !== 'production' && (
        <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Debug</h3>
            <button
              type="button"
              onClick={() => setShowDebug(prev => !prev)}
              className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded"
            >
              {showDebug ? 'Hide' : 'Show'}
            </button>
          </div>
          {showDebug && (
            <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 overflow-auto">
              {JSON.stringify(settings, null, 2)}
            </pre>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
        {/* Main Settings */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              S3 Configuration
            </h2>

            {/* Enable S3 */}
            <ToggleField
              label="Enable AWS S3 Storage"
              value={settings.enabled}
              onChange={handleSettingChange('enabled')}
              description="When enabled, all file uploads will be stored in AWS S3 instead of local storage"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Access Key ID"
                value={settings.accessKeyId}
                onChange={handleSettingChange('accessKeyId')}
                placeholder="AKIA..."
                required
                disabled={!settings.enabled}
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Access Key <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecretKey ? 'text' : 'password'}
                    value={settings.secretAccessKey || ''}
                    onChange={(e) => handleSettingChange('secretAccessKey')(e.target.value)}
                    placeholder="••••••••••••"
                    disabled={!settings.enabled}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showSecretKey ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Bucket Name"
                value={settings.bucketName}
                onChange={handleSettingChange('bucketName')}
                placeholder="my-bucket-name"
                required
                disabled={!settings.enabled}
              />
              
              <SelectField
                label="Region"
                value={settings.region}
                onChange={handleSettingChange('region')}
                options={[
                  { value: 'us-east-1', label: 'US East (N. Virginia)' },
                  { value: 'us-east-2', label: 'US East (Ohio)' },
                  { value: 'us-west-1', label: 'US West (N. California)' },
                  { value: 'us-west-2', label: 'US West (Oregon)' },
                  { value: 'eu-west-1', label: 'Europe (Ireland)' },
                  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
                  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
                  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
                  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' }
                ]}
              />
            </div>

            <InputField
              label="Endpoint (Optional)"
              value={settings.endpoint}
              onChange={handleSettingChange('endpoint')}
              placeholder="https://s3.amazonaws.com"
              description="Custom endpoint for S3-compatible services like MinIO"
              disabled={!settings.enabled}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ToggleField
                label="Force Path Style"
                value={settings.forcePathStyle}
                onChange={handleSettingChange('forcePathStyle')}
                description="Use path-style URLs instead of virtual-hosted-style"
              />
              
              <ToggleField
                label="Use Transfer Acceleration"
                value={settings.useAccelerateEndpoint}
                onChange={handleSettingChange('useAccelerateEndpoint')}
                description="Enable S3 Transfer Acceleration for faster uploads"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Encryption"
                value={settings.encryption}
                onChange={handleSettingChange('encryption')}
                options={[
                  { value: 'AES256', label: 'AES256 (Server-Side)' },
                  { value: 'aws:kms', label: 'AWS KMS' }
                ]}
              />
              
              <ToggleField
                label="Public Read Access"
                value={settings.publicRead}
                onChange={handleSettingChange('publicRead')}
                description="Make uploaded files publicly readable"
              />
            </div>

            <InputField
              label="CDN Domain (Optional)"
              value={settings.cdnDomain}
              onChange={handleSettingChange('cdnDomain')}
              placeholder="cdn.example.com"
              description="Custom CDN domain for serving S3 files"
              disabled={!settings.enabled}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Upload Prefix"
                value={settings.uploadPrefix}
                onChange={handleSettingChange('uploadPrefix')}
                placeholder="uploads"
                description="Prefix for uploaded files in S3"
                disabled={!settings.enabled}
              />
              
              <InputField
                label="Max File Size (bytes)"
                type="number"
                value={settings.maxFileSize}
                onChange={handleSettingChange('maxFileSize')}
                placeholder="10485760"
                description="Maximum file size in bytes (10MB = 10485760)"
                disabled={!settings.enabled}
              />
            </div>

            <InputField
              label="Allowed MIME Types"
              value={settings.allowedMimeTypes}
              onChange={handleSettingChange('allowedMimeTypes')}
              placeholder="image/*,application/pdf,text/*"
              description="Comma-separated list of allowed MIME types"
              disabled={!settings.enabled}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Storage Status
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">S3 Status:</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    settings.enabled 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {settings.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                
                {settings.enabled && settings.bucketName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bucket:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {settings.bucketName}
                    </span>
                  </div>
                )}
                
                {settings.enabled && settings.region && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Region:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {settings.region}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Actions
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={handleTestConnection}
                  disabled={testing || !settings.enabled}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                <Globe className="h-4 w-4 inline mr-1" />
                Important Notes
              </h4>
              <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <li>• S3 bucket must be created before enabling</li>
                <li>• IAM user must have S3 read/write permissions</li>
                <li>• CORS must be configured for web access</li>
                <li>• Files are automatically migrated when enabled</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mt-6 p-4 rounded-lg flex items-center ${
          saveStatus === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
        }`}>
          {saveStatus === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertTriangle className="h-5 w-5 mr-2" />
          )}
          {saveMessage}
        </div>
      )}
    </div>
  );
};

export default S3Settings;
